<template>
    <div class="h-100 w-100">
        <l-map :bounds="displayBounds" :padding="[0, 50]" :max-zoom="21" :options="mapOptions" ref="map" @click="handleCloseContextMenu"
            @zoomstart="handleCloseContextMenu" @movestart="handleCloseContextMenu" @move="handleMove" @moveend="handleMoveEnd">
            <div v-if="displayMapped && displayTrackers">
                <l-tile-layer :url="url" :attribution="attribution" :options="tileLayerOptions"></l-tile-layer>
                <template v-for="(subarray, key) in displayTrackers">
                    <subarray :key="key" :trackers="subarray" :toggleMode="toggleMode" :currentContextMenu="currentContextMenu"  :selectedTrackers="selectedTrackers[key]" :infoActive="infoActive" :action="action"
                        :angle="angle" :panning="panning"
                        @click="(...args) => handleMappedClick(key, ...args)"
                        @contextmenu="handleContextMenu">
                    </subarray>
                </template>
            </div>
            <div v-else-if="!displayMapped && displayTrackers">
                <subarray :trackers="displayUnmappedTrackers" :toggleMode="toggleMode" :currentContextMenu="currentContextMenu" :selectedTrackers="selectedTrackers" :infoActive="infoActive" :action="action"
                    @click="(...args) => handleUnmappedClick(...args)" :panning="panning"
                    @contextmenu="handleContextMenu">
                </subarray>
            </div>
            <l-control position="topleft" v-if="displayTrackers">
                <b-form-radio-group buttons v-model="toggleMode" :options="toggleOptions" :size="$feature.mobile ? 'sm' : null" />
            </l-control>
            <l-control position="topleft" v-if="displayTrackers">
                <b-button-group :size="$feature.mobile ? 'sm' : null">
                    <b-button @click="action = 'Select All'">Select All</b-button>
                    <b-button @click="action = 'Deselect All'">Deselect All</b-button>
                </b-button-group>
            </l-control>
            <l-control position="bottomleft" v-if="displayTrackers">
                <b-button-group :size="$feature.mobile ? 'sm' : null">
                    <b-button @click="handleHome"><i class="icon-home" /></b-button>
                    <b-button @click="handleInfo" :class="{'ms-btn-primary': infoActive}"><i class="icon-info" /></b-button>
                    <b-button v-if="statusSupportedGCUs.length > 0" @click="handleRefresh"><i class="icon-refresh" /></b-button>
                </b-button-group>
            </l-control>
            <l-control v-if="infoActive" position="topright">
                <b-button v-if="!isLegendVisible" @click="isLegendVisible=true" :size="$feature.mobile ? 'sm' : null"><i class="icon-question" /></b-button>
                <div v-if="isLegendVisible" class="legend">
                    <i class="icon-question" style="float: right; padding: 4px 4px; color: #000000;" @click="isLegendVisible=false" />
                    <table>
                        <tbody>
                            <tr>
                                <th>Panel</th>
                                <th>Status</th>
                            </tr>
                            <tr v-for="(error, index) in armErrors" :key="'panel' + index">
                                <td :style="{ backgroundColor: error.color }"></td>
                                <td>{{ error.title }}</td>
                            </tr>
                            <tr>
                                <th>Tracker</th>
                                <th>Status</th>
                            </tr>
                            <tr v-for="(error, index) in trackerErrors" :key="'tracker' + index">
                                <td :style="{ backgroundColor: error.color }"></td>
                                <td>{{ error.title }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </l-control>
            <div class="leaflet-control-loader" v-if="!displayTrackers && readyToLoad">
                <img src="img/msi_spinner.gif" height="60" width="60" class="img-map-loader">
            </div>
        </l-map>
    </div>
</template>

<script>
import { LMap, LTileLayer, LControl } from 'vue2-leaflet';
import Subarray from './Subarray';
import { generateCoordsUnmapped, computeMapDisplayBounds } from './helpers';
import _ from 'lodash';
import { mapGetters } from 'vuex';

export default {
    name: 'SiteMap',
    components: {
        LMap,
        LTileLayer,
        LControl,
        Subarray
    },
    props: {
        displayKey: {
            type: String
        },
        displayTrackers: {
            type: Object | Array
        },
        displayMapped: {
            type: Boolean,
            default: true
        },
        angle: {
            type: Number,
            default: 0
        },
        readyToLoad: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            url: `https://{s}.tiles.mapbox.com/v4/nicolasmartinezv.lk2boopa/{z}/{x}/{y}.png?access_token=${process.env.VUE_APP_MAPBOX_API_KEY}`,
            attribution: '<a href="https://www.mapbox.com/about/maps/" target="_blank">&copy; Mapbox &copy; OpenStreetMap</a> <a class="mapbox-improve-map" href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>',
            action: null,
            toggleMode: 'Single',
            toggleOptions: [
                { text: 'Single', value: 'Single' },
                { text: 'Column', value: 'Column' },
                { text: 'Row', value: 'Row' },
                { text: 'Subarray', value: 'Subarray' }
            ],
            mapOptions: { zoomControl: false, zoomSnap: 0.1, doubleClickZoom: false },
            tileLayerOptions: { maxZoom: 21 },
            selectedTrackers: {},
            infoActive: false,
            armErrors: [
                { color: '#9FBf86', title: 'Healthy' },
                { color: '#FF3333', title: 'Azimuth Error' },
                { color: '#A64DA6', title: 'Elevation Error' },
                { color: '#A58A74', title: 'Other Error' }
            ],
            trackerErrors: [
                { color: '#777777', title: 'No Response' },
                { color: '#e1f7d5', title: 'Tracking' },
                { color: '#f0ad4e', title: 'Stow' },
                { color: '#ccc9c9', title: 'Idle' },
                { color: '#337ab7', title: 'Sleep' },
                { color: '#ff0000', title: 'Error' },
                { color: '#7F5839', title: 'Other Error' }
            ],
            isLegendVisible: false,
            currentContextMenu: null,
            panning: false
        }
    },
    methods: {
        handleMappedClick(subarrayId, selectedSubarrayTrackers, isCtrlKey) {
            if (!isCtrlKey && this.action !== 'Select All') {
                this.selectedTrackers = {};
            }

            this.$set(this.selectedTrackers, subarrayId, selectedSubarrayTrackers);

            const selectedTrackers = {};
            Object.keys(this.selectedTrackers).forEach((subarrayId) => {
                Object.assign(selectedTrackers, this.selectedTrackers[subarrayId]);
            });

            if (this.action === 'Select All') {
                if (this.displayTrackersLength === Object.keys(selectedTrackers).length) {
                    this.action = null;
                }
            } else {
                this.action = null;
            }

            if (_.isEmpty(selectedTrackers)) {
                this.$emit('selectedTrackers', null);
            } else {
                this.$emit('selectedTrackers', selectedTrackers);
            }
        },
        handleUnmappedClick(selectedSubarrayTrackers) {
            this.selectedTrackers = selectedSubarrayTrackers;

            if (this.action === 'Select All' || this.action === 'Deselect All') {
                this.action = null;
            }

            if (_.isEmpty(this.selectedTrackers)) {
                this.$emit('selectedTrackers', null);
            } else {
                this.$emit('selectedTrackers', this.selectedTrackers);
            }
        },
        handleHome() {
            if (this.$refs.map.mapObject && this.displayBounds) {
                this.$refs.map.mapObject.fitBounds(this.displayBounds, { padding: [0, 50]});
            }
        },
        handleInfo() {
            this.infoActive = !this.infoActive;
            this.isLegendVisible = false;
        },
        handleRefresh() {
            this.$store.dispatch('savanna/gcus/refreshGCUTrackers', this.statusSupportedGCUs);
        },
        handleContextMenu(tracker) {
            let {clientX: x, clientY: y} = tracker.origin.originalEvent;
            this.currentContextMenu = {
                gcu_code: tracker.gcu_code,
                lcu_code: tracker.lcu_code,
                pos: {x, y}
            };
        },
        handleCloseContextMenu() {
            this.currentContextMenu = null;
        },
        handleMove() {
          if(!this.$feature.mobile) this.panning = true;
        },
        handleMoveEnd() {
          if(!this.$feature.mobile) this.panning = false;
        }
    },
    computed: {
        ...mapGetters('savanna/gcus', {
            isGCUStatusSupported: 'isGCUStatusSupported'
        }),
        displayBounds() {
            if (!this.displayTrackers) return;

            if (this.displayMapped) {
                return computeMapDisplayBounds(this.displayTrackers, this.displayMapped)
            } else {
                if (!this.displayUnmappedTrackers) return;
                return computeMapDisplayBounds(this.displayUnmappedTrackers, this.displayMapped)
            }
        },
        displayUnmappedTrackers() {
            if (!this.displayTrackers) return;
            if (!this.displayMapped) return generateCoordsUnmapped(this.displayTrackers);
        },
        displayTrackersLength() {
            if (!this.displayTrackers) return 0;

            let length = 0;
            Object.keys(this.displayTrackers).forEach((subarrayId) => {
                length += this.displayTrackers[subarrayId].length;
            });

            return length;
        },
        statusSupportedGCUs() {
            if (!this.displayTrackers) return [];

            const gcus = [];

            if (this.displayMapped) {
                Object.keys(this.displayTrackers).forEach((subarrayId) => {
                    const gcu = this.displayTrackers[subarrayId][0].gcu_code;
                    const found = gcus.find((gcuId) => gcuId === gcu);
                    if (!found && this.isGCUStatusSupported(gcu)) {
                        gcus.push(gcu);
                    }
                });
            } else {
                if (this.displayTrackers.length > 0) {
                    const gcu = this.displayTrackers[0].gcu_code;
                    if (this.isGCUStatusSupported(gcu)) {
                        gcus.push(gcu);
                    }
                }
            }

            return gcus;
        }
    },
    watch: {
        displayKey() {
            this.selectedTrackers = {};
            this.action = null;
            this.$emit('selectedTrackers', null);
        }
    }
}
</script>

<style scoped>
.legend {
    background-color:white;
    opacity:0.8;
}

.legend table {
    text-align:center;
    width: 150px;
    height:250px;
    border-radius: 5px;
    border-collapse: separate!important;
}

.legend tr {
    border:hidden 3px transparent;
    border-radius:5px;
    border-spacing:15px;
}
.legend td {
    border:hidden 3px transparent;
    border-radius:5px
}

.leaflet-control-loader {
	position: absolute;
	top: 50%;
	left: 50%;
	margin-top: -40px;
	margin-left: -50px;
	height: 110px;
	width: 130px;
	border-radius: 10px;
	background: rgb(255, 255, 255);
    z-index: 1000;
}

.img-map-loader {
    position: absolute;
	top: 50%;
	left: 50%;
    margin-top: -30px;
	margin-left: -30px;
}
</style>

<style>
.leaflet-container {
  background-color: rgb(232, 224, 216);
}
</style>
